// components/Form2307Management.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Form2307Form from './Form2307Form';
import formAPI from '../api/formAPI';

const QUARTER_MONTHS = {
  1: [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
  ],
  2: [
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
  ],
  3: [
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
  ],
  4: [
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ],
};

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);

// ─── Edit Modal ──────────────────────────────────────────────────────────────
const EditModal = ({ record, onSave, onClose }) => {
  const [form, setForm] = useState({
    description: record.description || '',
    baseAmount: record.baseAmount ?? '',
    withholdingRate: record.withholdingRate ?? '',
    taxWithheld: record.taxWithheld ?? '',
    certificateNumber: record.certificateNumber || '',
    atcCode: record.atcCode || '',
    status: record.status || 'draft',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'baseAmount' || field === 'withholdingRate') {
        const base = parseFloat(field === 'baseAmount' ? value : prev.baseAmount) || 0;
        const rate = parseFloat(field === 'withholdingRate' ? value : prev.withholdingRate) || 0;
        next.taxWithheld = ((base * rate) / 100).toFixed(2);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(record._id, {
        description: form.description,
        baseAmount: parseFloat(form.baseAmount) || 0,
        withholdingRate: parseFloat(form.withholdingRate) || 0,
        taxWithheld: parseFloat(form.taxWithheld) || 0,
        certificateNumber: form.certificateNumber,
        atcCode: form.atcCode,
        status: form.status,
      });
      onClose();
    } catch (err) {
      alert('Failed to save: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Form 2307 Record</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Base Amount (₱)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.baseAmount}
                onChange={(e) => handleChange('baseAmount', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rate (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.withholdingRate}
                onChange={(e) => handleChange('withholdingRate', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tax Withheld (₱) — auto-computed</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.taxWithheld}
              readOnly
              className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-gray-50 text-gray-600"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Certificate #</label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => handleChange('certificateNumber', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">ATC Code</label>
              <input
                type="text"
                value={form.atcCode}
                onChange={(e) => handleChange('atcCode', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              {['draft', 'received', 'used_for_credit', 'expired', 'cancelled'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Form2307Management = ({ client }) => {
  // Defaults to 'records', not the manual form. Certificates are now produced
// automatically when a VAT transaction with an ATC is posted, so the normal
// landing view is the list of what already exists — the hand-keying tab is a
// correction path, not the encoding workflow.
  const [activeTab, setActiveTab] = useState('records');
  const [correctionReason, setCorrectionReason] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const currentYear = new Date().getFullYear();

  const [selectedPeriod, setSelectedPeriod] = useState({
    quarter: null,
    year: currentYear,
    month: null,
  });

  const availableYears = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - i),
    [currentYear]
  );

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    if (!client?._id) return;
    try {
      setLoading(true);
      const params = {
        fiscalYear: selectedPeriod.year,
        limit: 2000,
        page: 1,
      };
      if (selectedPeriod.quarter) params.quarter = selectedPeriod.quarter;
      if (selectedPeriod.month) params.month = selectedPeriod.month;

      const res = await formAPI.getForm2307ByClient(client._id, params);
      const data =
        res?.data?.data?.records ||
        res?.data?.records ||
        res?.records ||
        [];
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching 2307 records:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter, selectedPeriod.month]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // ── Period helpers ─────────────────────────────────────────────────────────
  const handleQuarterChange = (value) => {
    const q = value ? Number(value) : null;
    setSelectedPeriod((p) => ({ ...p, quarter: q, month: null }));
  };

  const handleMonthChange = (value) => {
    setSelectedPeriod((p) => ({ ...p, month: value ? Number(value) : null }));
  };

  const handleYearChange = (value) => {
    setSelectedPeriod((p) => ({ ...p, year: Number(value) }));
  };

  const resetPeriod = () => {
    setSelectedPeriod({ quarter: null, year: currentYear, month: null });
  };

  // ── Filtered records (local filter on top of fetched set) ──────────────────
  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      if (selectedPeriod.quarter) {
        const q = r.month <= 3 ? 1 : r.month <= 6 ? 2 : r.month <= 9 ? 3 : 4;
        if (q !== selectedPeriod.quarter) return false;
      }
      if (selectedPeriod.month && r.month !== selectedPeriod.month) return false;
      return true;
    });
  }, [records, selectedPeriod]);

  // ── Summary computation ────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const totalBaseAmount = filteredRecords.reduce((acc, r) => acc + (Number(r.baseAmount) || 0), 0);
    const totalTaxWithheld = filteredRecords.reduce(
      (acc, r) => acc + (Number(r.taxWithheld) || 0),
      0
    );
    const totalNetAmount = totalBaseAmount - totalTaxWithheld;

    const byAtc = {};
    filteredRecords.forEach((r) => {
      const atc = r.atcCode || 'N/A';
      if (!byAtc[atc]) byAtc[atc] = { baseAmount: 0, taxWithheld: 0, count: 0 };
      byAtc[atc].baseAmount += Number(r.baseAmount) || 0;
      byAtc[atc].taxWithheld += Number(r.taxWithheld) || 0;
      byAtc[atc].count += 1;
    });

    return {
      totalBaseAmount,
      totalTaxWithheld,
      totalNetAmount,
      recordCount: filteredRecords.length,
      byAtc,
    };
  }, [filteredRecords]);

  // ── Actions ────────────────────────────────────────────────────────────────
  // The list above is filtered to selectedPeriod's quarter/month. Since the
  // backend derives each record's real period from its own transactionDate
  // (not a batch-level selection), a record saved while a specific
  // quarter/month filter is active can correctly belong elsewhere — jump the
  // view to match so it doesn't look like the save silently failed.
  const handleRecordAdded = useCallback((savedRecords = []) => {
    fetchRecords();

    if (!Array.isArray(savedRecords) || savedRecords.length === 0) return;

    const periods = new Set(
      savedRecords
        .filter((r) => r?.month && r?.fiscalYear)
        .map((r) => {
          const q = r.month <= 3 ? 1 : r.month <= 6 ? 2 : r.month <= 9 ? 3 : 4;
          return `${q}-${r.fiscalYear}`;
        })
    );

    if (periods.size === 1) {
      const [savedQuarter, savedYear] = Array.from(periods)[0].split('-').map(Number);
      const filterActive = selectedPeriod.quarter || selectedPeriod.month;
      if (filterActive && (savedQuarter !== selectedPeriod.quarter || savedYear !== selectedPeriod.year)) {
        setSelectedPeriod((prev) => ({ ...prev, quarter: savedQuarter, year: savedYear, month: null }));
      }
    }
  }, [fetchRecords, selectedPeriod.quarter, selectedPeriod.year, selectedPeriod.month]);

  const handleSaveEdit = async (recordId, updateData) => {
    await formAPI.updateForm2307Record(recordId, updateData);
    fetchRecords();
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Delete this Form 2307 record? This cannot be undone.')) return;
    try {
      setDeletingId(recordId);
      await formAPI.deleteForm2307Record(recordId);
      fetchRecords();
    } catch (err) {
      alert('Failed to delete: ' + (err?.message || err));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Period selector (shared by Records & Summary tabs) ────────────────────
  const PeriodSelector = () => (
    <div className="flex flex-wrap items-center gap-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1">Quarter</label>
        <select
          value={selectedPeriod.quarter ?? ''}
          onChange={(e) => handleQuarterChange(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          <option value="">All Quarters</option>
          <option value={1}>Q1 (Jan–Mar)</option>
          <option value={2}>Q2 (Apr–Jun)</option>
          <option value={3}>Q3 (Jul–Sep)</option>
          <option value={4}>Q4 (Oct–Dec)</option>
        </select>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Year</label>
        <select
          value={selectedPeriod.year}
          onChange={(e) => handleYearChange(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          {availableYears.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {selectedPeriod.quarter && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">Month</label>
          <select
            value={selectedPeriod.month ?? ''}
            onChange={(e) => handleMonthChange(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
          >
            <option value="">All Months</option>
            {(QUARTER_MONTHS[selectedPeriod.quarter] || []).map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="self-end">
        <button
          type="button"
          onClick={resetPeriod}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
        >
          Reset
        </button>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">
          Withholding Tax - Expanded (BIR Form 2307)
        </h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Manage EWT records for {client?.name}
        </p>
      </div>

      {/* Period selector (always visible) */}
      <div className="bg-white border rounded-lg p-4">
        <PeriodSelector />
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          {['records', 'summary', 'correction'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
              }`}
            >
              {tab === 'correction' ? 'CORRECTION / REISSUE' : tab.toUpperCase()}
            </button>
          ))}
        </nav>
      </div>

      {/* ── CORRECTION / REISSUE TAB ───────────────────────────────────────── */}
      {/*
        This was the ENCODE tab: staff keyed the sale or expense on the VAT
        screen, then re-keyed the same payee, amount and ATC here to get the
        certificate. A 2307 is now generated automatically when the transaction
        is posted, so that second pass is gone. The form survives for the cases
        that genuinely need hand-keying — correcting a certificate, or reissuing
        one — and the backend rejects a submission with no stated reason.
      */}
      {activeTab === 'correction' && (
        <div className="bg-white border rounded-lg">
          <div className="border-b border-ink-line bg-warning-50/40 p-4">
            <h3 className="text-sm font-medium text-ink">Corrections and reissues only</h3>
            <p className="text-sm text-ink-body mt-1">
              Form 2307 certificates are created automatically when you post a VAT transaction
              that has an ATC selected. Use this form only to correct or reissue one.
            </p>
            <label className="block text-xs font-medium text-ink-body mt-3 mb-1" htmlFor="correction-reason-2307">
              Reason for hand-keying this certificate <span className="text-danger-500">*</span>
            </label>
            <input
              id="correction-reason-2307"
              type="text"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              placeholder="e.g. Reissued after the payor corrected its TIN"
              className="w-full border border-ink-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {correctionReason.trim() ? (
            <Form2307Form
              client={client}
              onRecordAdded={handleRecordAdded}
              correctionReason={correctionReason.trim()}
              selectedQuarter={selectedPeriod.quarter}
              selectedYear={selectedPeriod.year}
              selectedMonth={selectedPeriod.month}
            />
          ) : (
            <div className="p-4 text-sm text-ink-body">
              Enter a reason above to open the correction form.
            </div>
          )}
        </div>
      )}

      {/* ── RECORDS TAB ────────────────────────────────────────────────────── */}
      {activeTab === 'records' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading records…</div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No records found for the selected period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      'Month',
                      'Vendor',
                      'TIN',
                      'Certificate #',
                      'ATC',
                      'Base Amount',
                      'Rate %',
                      'Tax Withheld',
                      'Date',
                      'Status',
                      'Actions',
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredRecords.map((r) => (
                    <tr key={r._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">{MONTH_NAMES[r.month] || r.month}</td>
                      <td className="px-3 py-2">
                        {r.vendor?.name || r.payerName || r.vendorName || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                        {r.vendor?.tin || r.payerTin || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.certificateNumber || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.atcCode || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        {formatCurrency(r.baseAmount)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-center">
                        {r.withholdingRate}%
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-teal-700">
                        {formatCurrency(r.taxWithheld)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                        {r.transactionDate
                          ? new Date(r.transactionDate).toLocaleDateString('en-PH')
                          : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.status === 'received'
                              ? 'bg-green-100 text-green-800'
                              : r.status === 'used_for_credit'
                              ? 'bg-blue-100 text-blue-800'
                              : r.status === 'expired' || r.status === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => setEditingRecord(r)}
                            className="text-teal-600 hover:text-teal-800"
                          >
                            {/* pencil */}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.415.586H7v-3.414a2 2 0 01.586-1.414z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            disabled={deletingId === r._id}
                            onClick={() => handleDelete(r._id)}
                            className="text-red-500 hover:text-red-700 disabled:opacity-40"
                          >
                            {/* trash */}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Totals row */}
                <tfoot className="bg-teal-50">
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-xs font-semibold text-teal-800 uppercase text-right">
                      Totals ({filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''})
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-teal-900">
                      {formatCurrency(summary.totalBaseAmount)}
                    </td>
                    <td />
                    <td className="px-3 py-2 text-right font-bold text-teal-900">
                      {formatCurrency(summary.totalTaxWithheld)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── SUMMARY TAB ────────────────────────────────────────────────────── */}
      {activeTab === 'summary' && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800 font-medium">Total Income Payment</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                ₱{summary.totalBaseAmount.toFixed(2)}
              </p>
              <p className="text-xs text-blue-600 mt-1">{summary.recordCount} record(s)</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
              <p className="text-sm text-yellow-800 font-medium">Total EWT Withheld</p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">
                ₱{summary.totalTaxWithheld.toFixed(2)}
              </p>
              <p className="text-xs text-yellow-600 mt-1">Creditable against ITR</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <p className="text-sm text-green-800 font-medium">Net Amount (After EWT)</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                ₱{summary.totalNetAmount.toFixed(2)}
              </p>
              <p className="text-xs text-green-600 mt-1">Base - EWT</p>
            </div>
          </div>

          {/* By ATC breakdown */}
          {Object.keys(summary.byAtc).length > 0 && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700">Breakdown by ATC Code</h4>
              </div>
              <table className="min-w-full text-sm divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ATC Code</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Base Amount</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax Withheld</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {Object.entries(summary.byAtc).map(([atc, data]) => (
                    <tr key={atc} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs">{atc}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(data.baseAmount)}</td>
                      <td className="px-4 py-2 text-right text-teal-700 font-medium">
                        {formatCurrency(data.taxWithheld)}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500">{data.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Important info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-medium">
              ℹ️ Form 2307 (EWT) is a CREDITABLE withholding tax. The total EWT withheld is applied
              as a tax credit against your Income Tax Return (ITR), NOT deducted from VAT or
              Percentage Tax payable.
            </p>
          </div>

          {/* Formula line */}
          <p className="text-xs text-gray-500 font-mono">
            ITR Tax Due − EWT Credits (Form 2307) = Net ITR Payable
          </p>
        </div>
      )}

      {/* Edit modal */}
      {editingRecord && (
        <EditModal
          record={editingRecord}
          onSave={handleSaveEdit}
          onClose={() => setEditingRecord(null)}
        />
      )}
    </div>
  );
};

export default Form2307Management;
